/**
 * Safely extracts an error message from an unknown caught value.
 * Handles Error instances, objects with a message property, and primitives.
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error && typeof (error as { message: unknown }).message === 'string') {
    return (error as { message: string }).message;
  }
  return String(error);
}

/**
 * Checks if an unknown caught value has an error code property.
 * Useful for Firebase errors which have .code.
 */
export function getErrorCode(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'code' in error && typeof (error as { code: unknown }).code === 'string') {
    return (error as { code: string }).code;
  }
  return undefined;
}

/**
 * Type guard: checks if an unknown value is an Error-like object with code, message, httpStatus.
 * Used in onboarding services to detect custom structured errors.
 */
export function isStructuredError(error: unknown): error is { code: string; message: string; httpStatus: number } {
  return (
    error !== null &&
    typeof error === 'object' &&
    'code' in error &&
    'message' in error &&
    'httpStatus' in error
  );
}
